import { useEffect, useRef, useState } from "react";
import BottomSheet from "../BottomSheet";
import { deleteBet } from "../../lib/delete-bet-client";
import { sgpMatchup } from "../../lib/bet-root-display";
import { JT } from "./jumbotronTokens";

// RemoveBetSheet (#1214 PR B, spec §16.3).
// Single job: confirm removing one bet, call the route, and report the outcome.
// Reads: target { betId, summary, settled } captured by the page when the sheet opened. Writes: nothing
// directly -- delete-bet-client calls the route; onRemoved(betId) hands the confirmed id to the
// page (which runs useBetRemoval.removeBet). Does NOT: remove anything before the server
// confirms, sign out on a 401, or depend on the originating card staying mounted (P1-5).
// One sheet per page: tickets and rows only call onRequestRemove(bet).
// #1731 B1 (spec R1.4, R2.5): the request carries the settled state the sheet SHOWS (`shownSettled`, from the target,
// then from the server). A state_changed answer (route gate, B2+) means the bet settled or un-settled since the sheet
// opened: nothing was deleted, the copy re-renders from the server's state with a notice, and the user taps again. No
// automatic retry. An answer for a bet the sheet no longer shows is dropped.

const COPY_PENDING = "Gone from My Slips, History and The Bookies. Can't be undone in the app.";
// #1731 B2 (spec R2.10, Nicky 2026-10-26 "Use it"): the record sentence ships in the same deploy as the counted route,
// which makes it true. Builds cut before B2 (build 30) keep B1's short copy: true, under-promising.
const COPY_SETTLED = "Gone from My Slips and History. Its result stays in your Bookies record unless it matches another bet in your record. Can't be undone in the app.";
// #1731 B1 (Nicky 2026-10-26, "Use it"). The notice sits above the copy it points at.
const NOTICE = {
  settled: "This bet has settled. Check the details below and tap Remove again.",
  unsettled: "This bet is no longer settled. Check the details below and tap Remove again.",
};

// Headline the ticket itself shows (SGP game, "N-Leg Parlay", the teams), then " · $stake".
// Fallbacks avoid the ticket's em dash placeholder (DESIGN.md: no em dashes in UI copy).
export function betSummary(bet) {
  const legs = Array.isArray(bet?.legs) ? bet.legs.length : 0;
  const headline = bet?.slipType === "sgp" ? (sgpMatchup(bet, null) || "Same Game Parlay")
    : bet?.slipType === "parlay" ? `${legs}-Leg Parlay`
    : (bet?.teams || "This bet");
  return `${headline} · $${Number(bet?.stake) || 0}`;
}

// The sheet sits inside the page's scroll container, whose touch handlers drive pull-to-refresh
// (App.js); React bubbles synthetic events through the component tree even from a fixed overlay.
const stopTouch = (e) => e.stopPropagation();

const btnBase = { width: "100%", minHeight: 52, borderRadius: 12, fontSize: 15, fontWeight: 800, fontFamily: "inherit", cursor: "pointer" };

export default function RemoveBetSheet({ target, onClose, onRemoved, haptic }) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // The settled state the sheet shows, owned by the target it belongs to. Synced during render (the #1223 pattern), not
  // in an effect, so a newly opened bet's first frame already shows its own copy and a fast tap sends its own state.
  // While the sheet animates closed (target null) the last bet's state is kept.
  const [settledFor, setSettledFor] = useState({ target, settled: !!target?.settled });
  if (target && settledFor.target !== target) setSettledFor({ target, settled: !!target.settled });
  const shownSettled = target && settledFor.target !== target ? !!target.settled : settledFor.settled;
  // A notice belongs to the bet it was raised for; another bet never renders it, even for one frame.
  const [noticeFor, setNoticeFor] = useState({ target: null, text: null });
  const notice = noticeFor.target === target ? noticeFor.text : null;
  const mountedRef = useRef(true);
  // Keep the last target on screen while the sheet animates closed (target is null by then).
  const shownRef = useRef(target);
  if (target) shownRef.current = target;
  const shown = shownRef.current;

  // Re-arm on every mount: React.StrictMode (development) runs this cleanup and re-mounts (Nova F-1).
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  useEffect(() => { setError(null); setSubmitting(false); }, [target]);
  // The bet this sheet currently shows; a late answer for another bet is dropped (#1731 B1).
  const targetRef = useRef(target);
  targetRef.current = target;

  const close = () => { if (!submitting) onClose(); };

  const confirm = async () => {
    if (!target || submitting) return;
    const asked = target;
    const { betId } = asked;
    setError(null);
    setNoticeFor({ target: null, text: null });
    setSubmitting(true);
    let result;
    try {
      // deleteBet never throws: it classifies every outcome and reports to Sentry itself.
      result = await deleteBet(betId, { expectSettled: shownSettled });
      // A server-confirmed removal reaches App state even if this page unmounted mid-request
      // (pre-Codex F1): onRemoved is App's stable removeBet, safe to call after unmount.
      if (result.removed) {
        onRemoved(betId);
        haptic?.light?.();
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
    if (!mountedRef.current) return;
    // An answer for a bet the sheet no longer shows changes nothing on screen (its removal was handed up above).
    if (targetRef.current !== asked) return;
    if (result.removed) { onClose(); return; }
    if (result.kind === "state_changed") {
      setSettledFor({ target: asked, settled: result.settled });
      setNoticeFor({ target: asked, text: result.settled ? NOTICE.settled : NOTICE.unsettled });
      return;
    }
    setError(result.message);
  };

  return (
    <div style={{ display: "contents" }} onTouchStart={stopTouch} onTouchMove={stopTouch} onTouchEnd={stopTouch}>
      <BottomSheet open={!!target} onClose={close} locked={submitting}>
        <div style={{ padding: "8px 20px 40px" }}>
          <div style={{ fontSize: 18, fontWeight: 700, lineHeight: 1.4, color: JT.text.primary }}>Remove this bet?</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: JT.text.body, marginTop: 6 }}>{shown ? shown.summary : ""}</div>
          {notice && (
            // Informational, not an error: neutral text (Signal Scarcity), announced politely.
            <div role="status" aria-live="polite" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5, color: JT.text.primary, marginTop: 12 }}>{notice}</div>
          )}
          <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.5, color: JT.text.secondary, marginTop: notice ? 6 : 12 }}>
            {/* A pending bet leaves The Bookies (the board lists open bets only); a settled bet's record claim returns
                with B2 (#1731). Settled bets are listed on HISTORY (ResultsTab); the Stats (RESEARCH) tab lists no bets (legal pass C1). The Bookies
                record is its own page (BookiesPage). */}
            {shownSettled ? COPY_SETTLED : COPY_PENDING}
          </div>
          {error && (
            <div role="alert" style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.5, color: JT.status.lost.color, marginTop: 12 }}>{error}</div>
          )}
          <button type="button" onClick={confirm} disabled={submitting} aria-busy={submitting}
            style={{ ...btnBase, marginTop: 20, background: JT.status.lost.dim, color: JT.status.lost.color, border: `1px solid ${JT.status.lost.border}`, opacity: submitting ? 0.6 : 1 }}>
            {submitting ? "Removing…" : "Remove bet"}
          </button>
          <button type="button" onClick={close} disabled={submitting}
            style={{ ...btnBase, marginTop: 8, background: "transparent", color: JT.text.tertiary, border: `1px solid ${JT.border.mid}`, fontWeight: 700 }}>
            Cancel
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}
